'use strict';

/**
 * Flow A main-menu Change Centre enable/disable.
 *
 * Legacy footer hid or blocked the location chip (location-gaps rows 1–7).
 * Navigation keeps the buttons in layout and disables them (no toast, no `d-none`).
 * EV-8630: modify-order URLs (customer or dealer-portal) → Disabled, including
 * ChangeVIN — postcode/dealer changes for an existing order must go through the
 * Accessory Update flow, so dealer-portal Accessory Update stays Enabled.
 * Customer Accessory Update stays Disabled. Does not touch Flow B
 * `.selected-honda-centre .change-centre`.
 */
export const changeCentreCtas = {
  /**
   * Location edit + circular location button on configure and summary main-menu.
   *
   * @private
   * @readonly
   */
  FLOW_A_SELECTOR: '.modal-view[view="main-menu"] .change-centre',

  /**
   * Independent disable reasons other than the modify-order URL gate (read live in {@link changeCentreCtas.apply}).
   *
   * @private
   *
   * @type {{ duplicateTab: boolean, assignedDealer: boolean, crossBorder: boolean }}
   */
  reasons: {
    duplicateTab: false,
    assignedDealer: false,
    crossBorder: false
  },

  /**
   * Merge disable reasons and paint. Any reason or customer modify → `disabled`.
   *
   * @param {Object} [patch]
   */
  patch(patch) {
    if (patch) {
      Object.assign(this.reasons, patch);
    }

    this.apply();
  },

  /**
   * Enable or disable Flow A Change Centre from the current reasons + modify-order URL.
   */
  apply() {
    var disable = this.shouldDisable();
    var $buttons = $(this.FLOW_A_SELECTOR);

    $buttons.prop('disabled', disable);
    $buttons.attr('aria-disabled', disable ? 'true' : 'false');
    $buttons.toggleClass('disabled', disable);
  },

  /**
   * Current page query. Callers that need more than one flag pass this through.
   *
   * @private
   *
   * @returns {URLSearchParams}
   */
  urlSearchParams() {
    return new URLSearchParams(window.location.search);
  },

  /**
   * Same outer modify URL gate as `modifyOrderURLParameters`, including ChangeVIN.
   *
   * @private
   *
   * @param {URLSearchParams} [params]
   *
   * @returns {boolean}
   */
  isModifyOrderUrl(params) {
    params = params || this.urlSearchParams();

    var modifyCar = params.get('modifyCar') || '';
    var modifyAccessories = params.get('modifyAccessories') || '';
    var orderId = params.get('orderId') || '';
    var action = params.get('action') || '';

    return !!(
      (modifyCar && orderId)
      || (modifyAccessories && orderId)
      || (action === 'Changeconfigurationtonewoffer' && orderId)
      || (action === 'changeVIN' && orderId)
    );
  },

  /**
   * Same dealer-portal gate as `modifyOrderURLParameters` (`userId` + `sessionId`).
   *
   * @private
   *
   * @param {URLSearchParams} [params]
   *
   * @returns {boolean}
   */
  isDealerPortalUrl(params) {
    params = params || this.urlSearchParams();

    var userId = params.get('userId') || '';
    var sessionId = params.get('sessionId') || '';

    return !!(userId && sessionId);
  },

  /**
   * Footer hid suburb+pencil only for customer modify (`orderId` without portal).
   *
   * @private
   *
   * @returns {boolean}
   */
  isCustomerModifyOrderUrl() {
    var params = this.urlSearchParams();

    return this.isModifyOrderUrl(params) && !this.isDealerPortalUrl(params);
  },

  /**
   * Dealer-portal Accessory Update: the flow where an existing order's postcode may be changed.
   *
   * @private
   *
   * @param {URLSearchParams} [params]
   *
   * @returns {boolean}
   */
  isDealerAccessoryUpdateUrl(params) {
    params = params || this.urlSearchParams();

    var modifyAccessories = params.get('modifyAccessories') || '';
    var orderId = params.get('orderId') || '';

    return !!(modifyAccessories && orderId) && this.isDealerPortalUrl(params);
  },

  /**
   * @private
   *
   * @returns {boolean}
   */
  shouldDisable() {
    var params = this.urlSearchParams();
    var isDealerAccessoryUpdate = this.isDealerAccessoryUpdateUrl(params);

    // Dealer Accessory Update: neither the modify-order nor the assigned-dealer lock applies
    return (this.isModifyOrderUrl(params) && !isDealerAccessoryUpdate)
      || this.reasons.duplicateTab
      || (this.reasons.assignedDealer && !isDealerAccessoryUpdate)
      || this.reasons.crossBorder;
  }
};
